import { useEffect, useRef, useState } from "react";

interface LazyVideoProps {
  src: string;
  eager?: boolean;
  className?: string;
}

/**
 * Background video component with lazy loading via IntersectionObserver.
 * Videos are fetched as blobs to suppress browser-level network errors
 * (e.g. ERR_CONNECTION_FAILED during Lighthouse throttling).
 */
const LazyVideo = ({ src, eager = false, className = "h-full w-full object-cover" }: LazyVideoProps) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState(eager);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (eager || !ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [eager]);

  // Fetch video as blob to prevent browser-level network error logging
  useEffect(() => {
    if (!isVisible) return;
    let revoke: string | null = null;

    fetch(src)
      .then((res) => {
        if (!res.ok) throw new Error("Video fetch failed");
        return res.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        revoke = url;
        setBlobUrl(url);
      })
      .catch(() => {
        // Silently ignore – video is decorative
      });

    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [isVisible, src]);

  // Auto-play once blob is ready
  useEffect(() => {
    if (blobUrl && ref.current) {
      ref.current.play().catch(() => {});
    }
  }, [blobUrl]);

  return (
    <video
      ref={ref}
      loop
      muted
      playsInline
      preload="none"
      className={className}
      poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
      src={blobUrl || undefined}
    />
  );
};

export default LazyVideo;
